import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getFileIcon } from "../../utils/fileIconUtils";
import { getLinkIcon } from "../../utils/businessCardUtils";
import { HiOutlineMail, HiOutlinePhone, HiOutlineUser } from "react-icons/hi";
import VCardGenerator from "./VCardGenerator";
import { LinkItem } from "./LinkManager";
import BusinessCardFooter from "./BusinessCardFooter";
import BusinessCardShareButton from "./BusinessCardShareButton";
import UserRegistrationSection from "./UserRegistrationSection";
import BookingLinkButton from "./BookingLinkButton";
import { BusinessCardBooking } from "../../types/BusinessCardTypes";
import BusinessCardContainer from "./BusinessCardContainer";
import BusinessCardEmailLogin from "../pages/businessCardView/BusinessCardEmailLogin";
import ExpandableDescription from "./ExpandableDescription";
import LanguageSelector from "./LanguageSelector";
import SmartLink from "./SmartLink";
import PreviewSafeAnchor from "./PreviewSafeAnchor";
import PreviewSafeButton from "./PreviewSafeButton";

interface BusinessCardData {
  jobTitle: string;
  description?: string;
  links: LinkItem[];
  uploadedFiles?: Array<{
    fileName: string;
    cdnPath: string;
    displayName: string;
  }>;
  allowVCardDownload?: boolean;
  enableUserRegistration?: boolean;
  showFooter?: boolean;
  booking?: BusinessCardBooking;
}

interface BusinessCardMinimalistDisplayProps {
  userData: {
    user_identification: string;
    first_name: string;
    last_name: string;
    email: string;
    phone?: string;
    avatar?: string;
  };
  businessCardData: BusinessCardData;
  companyLogo?: string;
  companyName?: string;
  companyDescription?: string;
  className?: string;
  isPreview?: boolean;
  processId?: string;
  enableUserRegistration?: boolean;
  entityIdentification: string;
  ownerIdentification: string;
  onEmailClick?: () => void;
}

const BusinessCardMinimalistDisplay: React.FC<
  BusinessCardMinimalistDisplayProps
> = (props) => {
  const {
    userData,
    businessCardData,
    companyLogo,
    companyName,
    companyDescription,
    className = "",
    isPreview = false,
    processId,
    enableUserRegistration = false,
    entityIdentification,
    ownerIdentification,
    onEmailClick,
  } = props;

  const { t } = useTranslation("clientView");
  const [showAllLinks, setShowAllLinks] = useState(false);
  const [showFiles, setShowFiles] = useState(false);
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const [isFlipping, setIsFlipping] = useState(false);
  const cardContentRef = useRef<HTMLDivElement>(null);

  const handleFlipToEmail = () => {
    if (isFlipping) return;
    setIsFlipping(true);
    setTimeout(() => {
      setShowEmailLogin(true);
      onEmailClick?.();
    }, 300);
    setTimeout(() => setIsFlipping(false), 600);
  };

  const handleFileDownload = (cdnPath: string, fileName: string) => {
    const link = document.createElement("a");
    link.href = cdnPath;
    link.download = fileName;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const primaryLinks = businessCardData.links?.slice(0, 3) || [];
  const additionalLinks = businessCardData.links?.slice(3) || [];
  const hasFiles =
    businessCardData.uploadedFiles && businessCardData.uploadedFiles.length > 0;

  const headerContent = (companyName || companyLogo || companyDescription) && (
    <div className="border-b border-gray-100">
      <div className="px-3 sm:px-4 py-2 sm:py-3 bg-gray-50 relative">
        <div className="absolute top-2 end-2 z-[9999]">
          <LanguageSelector variant="compact" />
        </div>
        <div className="flex items-center justify-center gap-3">
          {companyLogo && (
            <div className="w-8 h-8 flex-shrink-0">
              <img
                src={companyLogo}
                alt="Company Logo"
                className="w-full h-full object-contain rounded-lgm"
              />
            </div>
          )}
          <div className="text-center min-w-0">
            {companyName && (
              <p
                className={`${
                  companyName.length > 20 ? "text-xs" : "text-sm"
                } font-medium text-gray-700 uppercase tracking-wider text-start`}
              >
                <bdi>{companyName}</bdi>
              </p>
            )}
            {companyDescription && (
              <p className="text-xs text-gray-600 italic mt-1 max-w-72 mx-auto text-start">
                <bdi>"{companyDescription}"</bdi>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const mainContent = showEmailLogin ? (
    <div className="flex flex-col gap-3 sm:gap-4">
      <BusinessCardEmailLogin
        entityIdentification={entityIdentification}
        ownerIdentification={ownerIdentification}
        onBackToCard={() => {
          if (isFlipping) return;
          setIsFlipping(true);
          setTimeout(() => setShowEmailLogin(false), 300);
          setTimeout(() => setIsFlipping(false), 600);
        }}
      />
    </div>
  ) : (
    <div className="flex flex-col gap-3 sm:gap-4">
      <div className="pb-3 border-b border-gray-100 text-center">
        <div className="mb-4">
          {userData.avatar ? (
            <div className="relative">
              <img
                src={userData.avatar}
                alt={`${userData.first_name} ${userData.last_name}`}
                className="w-16 h-16 rounded-full object-cover mx-auto border border-gray-200 shadow-sm"
              />
            </div>
          ) : (
            <div className="relative w-16 h-16 mx-auto">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center border border-gray-200">
                {userData.first_name?.[0] || userData.last_name?.[0] ? (
                  <span className="text-lg font-medium text-gray-500 uppercase">
                    {userData.first_name?.[0]}
                    {userData.last_name?.[0]}
                  </span>
                ) : (
                  <HiOutlineUser className="w-8 h-8 text-gray-400" />
                )}
              </div>
            </div>
          )}
        </div>
        <div className="space-y-2">
          <h1 className="text-lg font-light text-gray-900 tracking-wide break-words">
            <bdi>
              {userData.first_name} {userData.last_name}
            </bdi>
          </h1>
          <p className="text-sm text-gray-600 font-medium break-words text-start">
            <bdi>{businessCardData.jobTitle}</bdi>
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <PreviewSafeAnchor
          href={`mailto:${userData.email}`}
          className="grid grid-cols-[auto_1fr] gap-3 w-full p-3 sm:p-4 bg-gray-50 hover:bg-gray-100 text-gray-800 rounded-lg shadow-sm hover:shadow-sm transition-all duration-200 items-center"
        >
          <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
            <HiOutlineMail className="w-5 h-5 text-gray-600" />
          </div>
          <div className="min-w-0">
            <p
              dir="auto"
              className="text-xs text-gray-500 font-medium text-start"
            >
              {t("key98")}
            </p>
            <p
              className="font-medium text-sm sm:text-base truncate"
              title={userData.email}
            >
              <bdi>{userData.email}</bdi>
            </p>
          </div>
        </PreviewSafeAnchor>

        {userData.phone && (
          <PreviewSafeAnchor
            href={`tel:${userData.phone}`}
            className="flex items-center gap-3 w-full p-3 sm:p-4 bg-gray-50 hover:bg-gray-100 text-gray-800 rounded-lg shadow-sm hover:shadow-sm transition-all duration-200"
          >
            <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
              <HiOutlinePhone className="w-5 h-5 text-gray-600" />
            </div>
            <p className="font-medium text-sm sm:text-base">
              <bdi>{userData.phone}</bdi>
            </p>
          </PreviewSafeAnchor>
        )}
      </div>

      {businessCardData.description && (
        <div className="px-1 py-2 bg-gray-50 rounded-lg border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
            <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide text-start">
              {t("key106")}
            </p>
          </div>

          <ExpandableDescription
            content={businessCardData.description}
            maxLength={150}
            maxLines={5}
            textClassName="text-sm text-gray-600 leading-relaxed"
            buttonClassName="mt-2 inline-flex items-center text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors duration-200"
            expandText={t("key97") as string}
            collapseText={t("key107") as string}
          />
        </div>
      )}

      {primaryLinks.length > 0 && (
        <div className="space-y-2">
          {primaryLinks.map((link, index) => (
            <SmartLink
              key={index}
              href={link.link}
              fallbackHref={link.fallbackLink}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 bg-gray-50 hover:bg-gray-100 text-gray-800 rounded-lg transition-all duration-200 group shadow-sm hover:shadow-sm"
            >
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-white flex items-center justify-center shadow-sm">
                {getLinkIcon(link, "text-gray-600")}
              </div>
              <p className="flex-1 text-sm font-medium truncate text-start">
                {link.label}
              </p>
              <div className="text-gray-400 font-bold opacity-0 group-hover:opacity-100 transition-opacity">
                →
              </div>
            </SmartLink>
          ))}
        </div>
      )}

      {additionalLinks.length > 0 && (
        <div>
          {showAllLinks && (
            <div className="space-y-2 mb-2">
              {additionalLinks.map((link, index) => (
                <SmartLink
                  key={index + 3}
                  href={link.link}
                  fallbackHref={link.fallbackLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 bg-gray-50 hover:bg-gray-100 text-gray-800 rounded-lg transition-all duration-200 group shadow-sm hover:shadow-sm"
                >
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-white flex items-center justify-center shadow-sm">
                    {getLinkIcon(link, "text-gray-600")}
                  </div>
                  <p className="flex-1 text-sm font-medium truncate text-start">
                    {link.label}
                  </p>
                  <div className="text-gray-400 font-bold opacity-0 group-hover:opacity-100 transition-opacity">
                    →
                  </div>
                </SmartLink>
              ))}
            </div>
          )}
          <div className="text-center">
            <PreviewSafeButton
              onClick={() => setShowAllLinks(!showAllLinks)}
              className="text-xs font-semibold text-gray-700 uppercase tracking-wide hover:text-gray-500 transition-colors cursor-pointer"
              dir="auto"
            >
              <span className="text-start">
                {showAllLinks
                  ? t("businessCard.showLess")
                  : t("businessCard.showMoreLinks", {
                      count: additionalLinks.length,
                    })}
              </span>
            </PreviewSafeButton>
            <div className="w-12 h-px bg-gray-300 mx-auto mt-1"></div>
          </div>
        </div>
      )}

      {hasFiles && (
        <div>
          <div className="text-center mb-2">
            <PreviewSafeButton
              onClick={() => setShowFiles(!showFiles)}
              className="text-xs font-semibold text-gray-700 uppercase tracking-wide hover:text-gray-500 transition-colors cursor-pointer"
            >
              <span dir="auto" className="text-start">
                {t("businessCard.files", {
                  count: businessCardData.uploadedFiles?.length || 0,
                })}
              </span>
            </PreviewSafeButton>
            <div className="w-12 h-px bg-gray-300 mx-auto mt-1"></div>
          </div>

          {showFiles && businessCardData.uploadedFiles && (
            <div className="space-y-2">
              {businessCardData.uploadedFiles.map((file, index) => (
                <PreviewSafeButton
                  key={index}
                  onClick={() =>
                    handleFileDownload(file.cdnPath, file.fileName)
                  }
                  className="w-full flex items-center gap-3 p-3 bg-gray-50 hover:bg-gray-100 text-gray-800 rounded-lg transition-all duration-200 group shadow-sm hover:shadow-sm text-left"
                  dir="auto"
                >
                  <div className="w-8 h-8 bg-gray-200 rounded-lg flex items-center justify-center group-hover:bg-gray-300">
                    <span className="text-sm">
                      {getFileIcon(file.fileName)}
                    </span>
                  </div>
                  <p className="flex-1 text-sm font-medium truncate text-start">
                    {file.displayName}
                  </p>
                  <div className="text-gray-400 font-bold opacity-0 group-hover:opacity-100 transition-opacity">
                    →
                  </div>
                </PreviewSafeButton>
              ))}
            </div>
          )}
        </div>
      )}

      {(() => {
        const showSaveContact = businessCardData.allowVCardDownload === true;
        const showShare = !!processId;
        if (!showSaveContact && !showShare) return null;

        return (
          <div
            className={`grid gap-2 ${
              showSaveContact && showShare ? "grid-cols-2" : "grid-cols-1"
            }`}
          >
            {showSaveContact && (
              <VCardGenerator
                data={{
                  firstName: userData.first_name,
                  lastName: userData.last_name,
                  jobTitle: businessCardData.jobTitle,
                  email: userData.email,
                  phone: userData.phone,
                  description: businessCardData.description,
                  links: businessCardData.links,
                  avatar: userData.avatar,
                }}
                buttonColor="slate"
              />
            )}
            {showShare && (
              <BusinessCardShareButton
                processId={processId}
                size="md"
                variant="secondary"
                logoUrl={companyLogo}
              />
            )}
          </div>
        );
      })()}

      <BookingLinkButton
        booking={businessCardData.booking}
        entityIdentification={entityIdentification}
        memberId={userData.user_identification}
      />

      {enableUserRegistration && processId && entityIdentification && (
        <UserRegistrationSection
          processId={processId}
          entityData={{
            entity_identification: entityIdentification,
            entity_name: companyName,
            entity_logo: companyLogo,
          }}
          theme="minimal"
          size="sm"
          onEmailLogin={handleFlipToEmail}
        />
      )}
    </div>
  );

  const frontContent = (
    <div className="w-full">
      <div
        className="
        w-full
        h-full
        min-h-0
        relative
        rounded-xl
        overflow-hidden
      "
      >
        {headerContent && (
          <div className="row-start-1 row-end-2">{headerContent}</div>
        )}

        <div className="row-start-2 row-end-3 min-h-0">
          <div
            ref={cardContentRef}
            className="overflow-y-auto touch-pan-y"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            <div className="px-3 sm:px-4 py-3 sm:py-4">{mainContent}</div>
          </div>
        </div>

        <div className="row-start-3 row-end-4">
          <BusinessCardFooter
            showFooter={businessCardData.showFooter as boolean}
            theme="default"
          />
        </div>
      </div>
    </div>
  );

  return (
    <BusinessCardContainer
      isPreview={isPreview}
      className={`w-full h-auto min-h-0 ${className}`}
      frontContent={frontContent}
      isFlipping={isFlipping}
      showEmailLogin={showEmailLogin}
    />
  );
};

export default BusinessCardMinimalistDisplay;
